import { BRAND } from '@app/shared';
import { useSession } from '../../app/SessionProvider';
import { useI18n } from '../../i18n/I18nProvider';
import type { UiKey } from '../../i18n/dict';
import { LangSwitch } from '../../ui/controls';
import {
  ArrowRight,
  CalendarDays,
  ChartColumn,
  Heart,
  ICON_STROKE,
  Search,
  ShieldCheck,
  Smartphone,
  Store,
  type LucideIcon,
} from '../../ui/icons';
import { Screen, ScrollArea } from '../../ui/Screen';

/**
 * Стартовый экран: два входа — клиентский («Записаться») и бизнеса («Принимать записи»).
 *
 * Вернулся 21 сентября 2026 по решению владельца, по макету. Клиент по QR-наклейке его
 * по-прежнему не видит: deep-link ведёт сразу на страницу заведения (см. `App.tsx`).
 */
export function StartScreen(props: {
  onBook: () => void;
  onBusiness: () => void;
  /** Вход в админку платформы. Виден только владельцу продукта (`session.isAdmin`). */
  onAdmin: () => void;
}): JSX.Element {
  const { t } = useI18n();
  const session = useSession();
  const isAdmin = session.state.status === 'ready' && session.state.user.isAdmin;

  const features: readonly [LucideIcon, UiKey][] = [
    [CalendarDays, 'startF1'],
    [Heart, 'startF2'],
    [ShieldCheck, 'startF3'],
    [Smartphone, 'startF4'],
  ];

  return (
    <Screen>
      <ScrollArea>
        <div className="flex justify-end px-4 pt-[calc(env(safe-area-inset-top,0px)+14px)]">
          <div className="w-fit">
            <LangSwitch />
          </div>
        </div>

        <div className="px-4 pt-6 text-center">
          <h1 className="text-[52px] font-extrabold leading-none tracking-[-0.04em]">{BRAND.name}</h1>
          <div className="mt-4 text-[22px] font-bold tracking-[-0.02em]">{t('startTagline')}</div>
        </div>

        <div className="grid grid-cols-2 gap-3 px-4 pt-10">
          <EntryCard icon={Search} title={t('startBookT')} sub={t('startBookS')} onClick={props.onBook} />
          <EntryCard icon={Store} title={t('startBizT')} sub={t('startBizS')} onClick={props.onBusiness} />
        </div>

        <div className="grid grid-cols-4 gap-1 px-2 pt-8">
          {features.map(([Icon, key]) => (
            <div key={key} className="flex flex-col items-center gap-2 text-center text-ink-2">
              <Icon size={26} strokeWidth={1.5} aria-hidden="true" />
              <div className="text-[11.5px] leading-[1.3]">{t(key)}</div>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-3 px-6 pt-8">
          <div className="h-px flex-1 bg-hair" />
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-ink-3">
            {t('startMore')}
          </div>
          <div className="h-px flex-1 bg-hair" />
        </div>

        {/* Вход в админку платформы — только владельцу продукта (перенесён сюда из профиля
            клиента: владелец открывает приложение и попадает в панель в один тап, не копаясь
            в клиентском профиле). ponytail: подпись строкой, экран русскоязычный и один. */}
        {isAdmin ? (
          <div className="px-4 pt-7">
            <LinkRow icon={ChartColumn} label="Админ-панель" onClick={props.onAdmin} />
          </div>
        ) : null}

        <div className="pb-6" />
      </ScrollArea>
    </Screen>
  );
}

function EntryCard(props: {
  icon: LucideIcon;
  title: string;
  sub: string;
  onClick: () => void;
}): JSX.Element {
  const Icon = props.icon;
  return (
    <button
      type="button"
      onClick={props.onClick}
      className="flex min-h-[260px] flex-col rounded-[20px] bg-surface p-4 text-left shadow-[inset_0_0_0_1px_var(--hair)] transition active:scale-[0.98]"
    >
      <div className="flex h-14 w-14 items-center justify-center rounded-[16px] bg-surface-2 text-accent">
        <Icon size={28} strokeWidth={ICON_STROKE} aria-hidden="true" />
      </div>
      <div className="mt-5 text-[19px] font-extrabold leading-[1.15] tracking-[-0.02em]">{props.title}</div>
      <div className="mt-2 text-[13px] leading-[1.4] text-ink-2">{props.sub}</div>
      <div className="mt-auto flex justify-end pt-4">
        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-accent text-on-accent">
          <ArrowRight size={20} strokeWidth={2.25} aria-hidden="true" />
        </span>
      </div>
    </button>
  );
}

export function LinkRow(props: { icon: LucideIcon; label: string; onClick: () => void }): JSX.Element {
  const Icon = props.icon;
  return (
    <button
      type="button"
      onClick={props.onClick}
      className="flex w-full items-center gap-3 rounded-card bg-surface px-3.5 py-3 text-left text-[14px] font-semibold shadow-[inset_0_0_0_1px_var(--hair)] transition active:bg-surface-2"
    >
      <Icon size={18} strokeWidth={ICON_STROKE} className="text-ink-2" aria-hidden="true" />
      <span className="flex-1">{props.label}</span>
      <span className="text-[18px] text-ink-3" aria-hidden="true">
        ›
      </span>
    </button>
  );
}
